import { DateTime } from 'luxon';
import { QueryClient } from '@tanstack/react-query';
import { type AsyncStorage } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { get, set, del, entries, createStore, type UseStore } from 'idb-keyval';
import { ClientError, GraphQLClient } from 'graphql-request';
import {
	getHTTPHeaders,
	GRAPHQL_URL,
	REFRESH_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_KEY,
} from '@/views/home/auth/Authentication.hooks';
import { ACCESS_TOKEN_KEY } from '@/constants';
import {
	clearTokens,
	msToTime,
	refreshAccessToken,
} from '@/templates/basic/auth/Auth.hooks';

const store = createStore('canvasflow_db', 'react-query-cache');
const storage = idbStorage(store);

export async function clearPersistStorage() {
	if (!storage.entries) {
		return;
	}

	for (const [key] of await storage.entries()) {
		await storage.removeItem(key);
	}
}

export const persister = createAsyncStoragePersister({
	storage,
	key: 'cf-query',
	throttleTime: 2 * 1000, // 2 Seconds
	// Just pass the data
	serialize: (persistedQuery: any) => persistedQuery,
	deserialize: (cache: any) => cache,
});

export const defaultQueryClient = new QueryClient({
	defaultOptions: {
		queries: {
			gcTime: 1000 * 2,
			staleTime: 1000 * 10,
			networkMode: 'online',
		},
	},
});

export class QueryRequest {
	public queryClient: QueryClient;
	public graphqlClient: GraphQLClient;
	public rebuild: () => void = () => {};

	constructor(queryClient: QueryClient, graphqlClient: GraphQLClient) {
		this.queryClient = queryClient;
		this.graphqlClient = graphqlClient;
	}

	async query<T, V extends Record<string, any> | undefined>(
		query: string,
		variables?: V,
		headers?: HeadersInit,
	): Promise<T> {
		try {
			return await this.graphqlClient.request<T>(
				query,
				variables,
				headers,
			);
		} catch (error) {
			if (!(error instanceof ClientError)) throw error;
			const unauthenticated = error.response.errors?.some(
				(err) =>
					err.message.includes('jwt expired') ||
					err.extensions?.code === 'UNAUTHENTICATED',
			);

			if (!unauthenticated) throw error;
			this.queryClient.invalidateQueries();
			try {
				const success = await refreshToken();
				if (!success) {
					throw new Error('Session expired. Please log in again.');
				}

				const token = localStorage.getItem(ACCESS_TOKEN_KEY);
				if (!token) {
					throw new Error('Session expired. Please log in again.');
				}

				this.graphqlClient.setHeader('Authorization', token);
				if (this.rebuild) {
					this.rebuild();
				}
				return await this.graphqlClient.request<T>(query, variables);
			} catch (refreshError) {
				clearTokens('/');
				return Promise.reject(
					new Error('Session expired. Please log in again.'),
				);
			}
		}
	}

	async queryWithClient<T, V extends Record<string, any> | undefined>(
		graphqlClient: GraphQLClient,
		query: string,
		variables?: V,
	): Promise<T> {
		try {
			return await graphqlClient.request<T>(query, variables);
		} catch (error) {
			if (!(error instanceof ClientError)) throw error;
			const unauthenticated = error.response.errors?.some(
				(err) =>
					err.message.includes('jwt expired') ||
					err.extensions?.code === 'UNAUTHENTICATED',
			);

			if (!unauthenticated) throw error;
			this.queryClient.invalidateQueries();
			try {
				const success = await refreshToken();
				if (!success) {
					throw new Error('Session expired. Please log in again.');
				}

				const token = localStorage.getItem(ACCESS_TOKEN_KEY);
				if (!token) {
					throw new Error('Session expired. Please log in again.');
				}

				const headers = getHTTPHeaders();

				graphqlClient = new GraphQLClient(GRAPHQL_URL, { headers });

				if (this.rebuild) {
					this.rebuild();
				}
				return await graphqlClient.request<T>(query, variables);
			} catch (refreshError) {
				clearTokens('/');
				return Promise.reject(
					new Error('Session expired. Please log in again.'),
				);
			}
		}
	}
}

export async function authRequest<T, V extends Record<string, any> | undefined>(
	queryClient: QueryClient,
	client: GraphQLClient,
	query: string,
	rebuildGraphQLClient: (() => void) | undefined = undefined,
	variables?: V,
): Promise<T> {
	try {
		return await client.request<T>(query, variables);
	} catch (error) {
		if (error instanceof ClientError) {
			const unauthenticated = error.response.errors?.some(
				(err) =>
					err.message.includes('jwt expired') ||
					err.extensions?.code === 'UNAUTHENTICATED',
			);

			if (unauthenticated) {
				queryClient.invalidateQueries();
				try {
					const success = await refreshToken();
					if (!success) {
						throw new Error(
							'Session expired. Please log in again.',
						);
					}

					const token = localStorage.getItem(ACCESS_TOKEN_KEY);
					if (!token) {
						throw new Error(
							'Session expired. Please log in again.',
						);
					}

					const headers: any = getHTTPHeaders();
					client = new GraphQLClient(GRAPHQL_URL, { headers });
					return await client.request<T>(query, variables);
				} catch (refreshError) {
					const token = localStorage.getItem(ACCESS_TOKEN_KEY);
					if (
						typeof refreshError === 'object' &&
						refreshError !== null &&
						'message' in refreshError
					) {
						console.log(token);
						console.log(client);
						console.error(refreshError.message);
						alert(refreshError.message);
					}

					clearTokens('/');
					return Promise.reject(
						new Error('Session expired. Please log in again.'),
					);
				}
			}
		}

		throw error;
	}
}

async function refreshToken(): Promise<boolean> {
	const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
	const refreshTokenExp = localStorage.getItem(REFRESH_TOKEN_EXPIRATION_KEY);

	if (!refreshToken || !refreshTokenExp) {
		throw new Error('Refresh token not available');
	}

	// Calculate expiration dates
	const refreshExpirationDate = DateTime.fromISO(refreshTokenExp);

	const now = DateTime.now();

	// If now is larger than refresh expiration date clear the tokens
	if (now >= refreshExpirationDate) {
		throw new Error('Refresh token expired');
	}

	try {
		const accessExpirationDate = await refreshAccessToken(refreshToken);
		const accessExpiration = DateTime.fromJSDate(
			new Date(accessExpirationDate),
		);

		const now = DateTime.now();
		const diff = accessExpiration.diff(now);
		console.log(`Token refreshed successfully`);
		console.debug(`Token is going to expire at: ${accessExpirationDate}`);

		console.debug(
			`The token is going to expire in: ${msToTime(
				diff.toObject().milliseconds,
			)}`,
		);
		return true;
	} catch (e) {
		console.error(e);
		return false;
	}
}

function idbStorage(idbStore: UseStore): AsyncStorage {
	return {
		getItem: async (key) => get(key, idbStore),
		setItem: async (key, value) => set(key, value, idbStore),
		removeItem: async (key) => del(key, idbStore),
		entries: async () => entries(idbStore),
	};
}
